import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import SEO from '../components/SEO'
import PageTransition from '../components/PageTransition'
import { FiUserPlus, FiUsers, FiLoader, FiUserX, FiCheck, FiX, FiClock, FiUserCheck } from 'react-icons/fi'
import SafeIcon from '../common/SafeIcon'
import FriendSearchModal from '../components/FriendSearchModal'
import FriendsSkeleton from '../components/skeletons/FriendsSkeleton'
import { getFriends, getFriendRequests, respondToFriendRequest, removeFriend, cancelFriendRequest } from '../services/friendService'
import AvatarBadge from '../components/AvatarBadge'
import { checkFriendCapacity, isPlanCapError, parsePlanCapError } from '../services/planService'
import PaywallModal from '../components/PaywallModal'

function Friends() {
  const navigate = useNavigate()
  const [friends, setFriends] = useState([])
  const [incomingRequests, setIncomingRequests] = useState([])
  const [outgoingRequests, setOutgoingRequests] = useState([])
  const [loading, setLoading] = useState(true)
  const [showSearchModal, setShowSearchModal] = useState(false)
  const [respondingId, setRespondingId] = useState(null)
  const [removingId, setRemovingId] = useState(null)
  const [cancellingId, setCancellingId] = useState(null)
  const [paywallFeature, setPaywallFeature] = useState(null)

  const loadData = useCallback(async () => {
    setLoading(true)
    try {
      const [friendsRes, incomingRes, outgoingRes] = await Promise.all([
        getFriends(),
        getFriendRequests('incoming'),
        getFriendRequests('outgoing'),
      ])
      setFriends(friendsRes.data)
      setIncomingRequests(incomingRes.data)
      setOutgoingRequests(outgoingRes.data)
    } catch (err) {
      console.error('Error loading friends data:', err)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadData()
  }, [loadData])

  const handleRespond = async (friendshipId, status) => {
    setRespondingId(friendshipId)
    try {
      // Free-tier preflight: only on accept. Declines are always allowed.
      if (status === 'accepted') {
        const cap = await checkFriendCapacity()
        if (!cap.allowed) {
          setPaywallFeature('friends')
          return
        }
      }
      const { error } = await respondToFriendRequest(friendshipId, status)
      if (error) {
        if (isPlanCapError(error)) {
          const parsed = parsePlanCapError(error)
          // 'other' = the requester is at-cap, not the responder.
          setPaywallFeature(parsed?.side === 'other' ? 'friends_other' : 'friends')
        }
        return
      }
      await loadData()
    } finally {
      setRespondingId(null)
    }
  }

  const handleRemove = async (friendUserId) => {
    if (!window.confirm('Are you sure you want to remove this friend?')) return
    setRemovingId(friendUserId)
    try {
      const { error } = await removeFriend(friendUserId)
      if (!error) {
        await loadData()
      }
    } finally {
      setRemovingId(null)
    }
  }

  const handleCancel = async (friendshipId) => {
    if (!window.confirm('Cancel this friend request?')) return
    setCancellingId(friendshipId)
    try {
      const { error } = await cancelFriendRequest(friendshipId)
      if (!error) {
        await loadData()
      }
    } finally {
      setCancellingId(null)
    }
  }

  // Combine the three lists into one unified collection with a status
  // discriminator so we can render a single grid. Sort order: incoming
  // requests first (most actionable), then friends (alphabetical), then
  // outgoing/sent requests.
  const combinedItems = useMemo(() => {
    const incoming = incomingRequests.map((r) => ({ ...r, status: 'incoming' }))
    const sent = outgoingRequests.map((r) => ({ ...r, status: 'sent' }))
    const friendsList = [...friends]
      .map((f) => ({ ...f, status: 'friend' }))
      .sort((a, b) => (a.full_name || '').localeCompare(b.full_name || ''))
    return [...incoming, ...friendsList, ...sent]
  }, [friends, incomingRequests, outgoingRequests])

  const totalCount = combinedItems.length

  if (loading) {
    return (
      <>
        <SEO title="Friends | TeeSync" noindex={true} />
        <FriendsSkeleton />
      </>
    )
  }

  const renderStatusPill = (status) => {
    if (status === 'friend') {
      return (
        <span className="inline-flex items-center space-x-1 text-xs font-medium px-2 py-1 rounded-full bg-status-success/10 text-status-success">
          <SafeIcon icon={FiUserCheck} className="w-3 h-3" />
          <span>Friend</span>
        </span>
      )
    }
    if (status === 'incoming') {
      return (
        <span className="inline-flex items-center space-x-1 text-xs font-medium px-2 py-1 rounded-full bg-status-warning/10 text-status-warning">
          <span>Incoming</span>
        </span>
      )
    }
    return (
      <span className="inline-flex items-center space-x-1 text-xs font-medium px-2 py-1 rounded-full bg-surface text-text-muted">
        <SafeIcon icon={FiClock} className="w-3 h-3" />
        <span>Sent</span>
      </span>
    )
  }

  const renderCardActions = (item) => {
    if (item.status === 'friend') {
      return (
        <button
          onClick={() => handleRemove(item.friend_id)}
          disabled={removingId === item.friend_id}
          className="p-2 text-text-muted hover:text-status-error transition-colors rounded-lg hover:bg-surface disabled:opacity-50"
          title="Remove friend"
        >
          {removingId === item.friend_id ? (
            <SafeIcon icon={FiLoader} className="w-4 h-4 animate-spin" />
          ) : (
            <SafeIcon icon={FiUserX} className="w-4 h-4" />
          )}
        </button>
      )
    }
    if (item.status === 'incoming') {
      return (
        <div className="flex items-center space-x-2">
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={() => handleRespond(item.friendship_id, 'accepted')}
            disabled={respondingId === item.friendship_id}
            className="p-2 text-accent hover:bg-accent/10 rounded-lg transition-colors disabled:opacity-50"
            title="Accept"
          >
            {respondingId === item.friendship_id ? (
              <SafeIcon icon={FiLoader} className="w-5 h-5 animate-spin" />
            ) : (
              <SafeIcon icon={FiCheck} className="w-5 h-5" />
            )}
          </motion.button>
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={() => handleRespond(item.friendship_id, 'declined')}
            disabled={respondingId === item.friendship_id}
            className="p-2 text-text-muted hover:text-status-error hover:bg-surface rounded-lg transition-colors disabled:opacity-50"
            title="Decline"
          >
            <SafeIcon icon={FiX} className="w-5 h-5" />
          </motion.button>
        </div>
      )
    }
    if (item.status === 'sent') {
      return (
        <button
          onClick={() => handleCancel(item.friendship_id)}
          disabled={cancellingId === item.friendship_id}
          className="p-2 text-text-muted hover:text-status-error transition-colors rounded-lg hover:bg-surface disabled:opacity-50"
          title="Cancel friend request"
        >
          {cancellingId === item.friendship_id ? (
            <SafeIcon icon={FiLoader} className="w-4 h-4 animate-spin" />
          ) : (
            <SafeIcon icon={FiX} className="w-4 h-4" />
          )}
        </button>
      )
    }
    return null
  }

  return (
    <>
      <SEO title="Friends | TeeSync" noindex={true} />
      <div className="min-h-screen bg-app">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <PageTransition className="space-y-6">
            <div className="flex justify-end">
              <button
                onClick={() => setShowSearchModal(true)}
                className="w-full sm:w-auto min-h-[44px] inline-flex items-center justify-center px-5 py-2.5 bg-accent text-white rounded-lg hover:bg-interactive-hover transition-colors font-medium text-sm"
              >
                <SafeIcon icon={FiUserPlus} className="w-4 h-4 mr-2" />
                Add Friend
              </button>
            </div>

            {/* Unified grid - incoming first, then friends (alphabetical),
                then sent. Each card carries a status pill in its top-right
                corner so the user can scan all relationships at once
                without flipping between tabs. */}
            {totalCount > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {combinedItems.map((item) => (
                  <div
                    key={`${item.status}-${item.friendship_id}`}
                    className="bg-card border border-border-subtle rounded-lg p-4 flex flex-col justify-between"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center space-x-3 min-w-0 flex-1">
                        <AvatarBadge name={item.full_name} size="md" />
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-text-primary truncate">{item.full_name || 'Unknown'}</p>
                          <p className="text-xs text-text-muted truncate">{item.email}</p>
                        </div>
                      </div>
                      <div className="ml-2 flex-shrink-0">{renderStatusPill(item.status)}</div>
                    </div>

                    {item.status === 'incoming' && item.message && (
                      <p className="mb-3 text-sm text-text-secondary italic">"{item.message}"</p>
                    )}

                    <div className="flex items-center justify-end pt-2 border-t border-border-subtle">
                      {renderCardActions(item) || (
                        <span className="text-xs text-text-muted">Awaiting response</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 bg-card rounded-lg border border-border-subtle">
                <div className="w-16 h-16 bg-surface rounded-lg flex items-center justify-center mx-auto mb-4 border border-border-subtle">
                  <SafeIcon icon={FiUsers} className="w-8 h-8 text-text-muted" />
                </div>
                <h3 className="text-h3 text-text-primary mb-2">No friends or requests yet</h3>
                <p className="text-sm text-text-muted">Add a friend to start scheduling rounds together.</p>
              </div>
            )}
          </PageTransition>
        </div>
      </div>

      <FriendSearchModal
        isOpen={showSearchModal}
        onClose={() => setShowSearchModal(false)}
        onRequestSent={loadData}
      />

      <PaywallModal
        isOpen={!!paywallFeature}
        onClose={() => setPaywallFeature(null)}
        feature={paywallFeature}
      />
    </>
  )
}

export default Friends
